import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import config from '../config/api.config';
import LogoTypeSmart from '../assets/images/LogoTypeSmart';
import { Lock, User, Sparkles } from 'lucide-react';

const DemoLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [username, setUsername] = useState('demo');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const baseUrl = config.API_URL.replace(/\/api\/?$/, '');
      const response = await fetch(`${baseUrl}/api/auth/demo-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.detail || 'ورود ناموفق بود');
      }
      const data = await response.json();
      AuthService.saveTokens(data);
      if (data.user) {
        AuthService.saveUserInfo(data.user);
      }
      await checkAuth();
      navigate('/dashboard/main?brandId=9001&branchId=900103', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در ورود');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-primary p-4 sm:p-6" dir="rtl">
      <div className="w-full max-w-md animate-enter-up">
        <div className="flex flex-col items-center mb-8">
          <LogoTypeSmart fill="var(--text-primary)" width={160} height={22} onClick={() => undefined} />
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-border-primary bg-bg-secondary px-3 py-1 text-xs text-text-secondary">
            <Sparkles className="w-3.5 h-3.5 text-accent-purple" aria-hidden="true" />
            <span>محیط نمایشی Smart Insight</span>
          </div>
        </div>

        <div className="card-base card-padding shadow-lg border border-border-primary">
          <div className="mb-6 text-center">
            <h1 className="title-section mb-2">ورود به Insight</h1>
            <p className="text-sm text-text-secondary leading-relaxed">
              با حساب نمایشی وارد شوید و داشبوردهای تحلیلی را بدون اتصال به داده واقعی بررسی کنید.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="demo-username" className="block text-sm font-medium mb-2 text-text-secondary">
                نام کاربری
              </label>
              <div className="relative">
                <User
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  id="demo-username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full rounded-lg border border-border-primary bg-bg-primary text-text-primary pr-10 pl-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-accent-cyan/40 focus:border-accent-cyan transition-colors"
                  autoComplete="username"
                />
              </div>
            </div>

            <div>
              <label htmlFor="demo-password" className="block text-sm font-medium mb-2 text-text-secondary">
                رمز عبور
              </label>
              <div className="relative">
                <Lock
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  id="demo-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-lg border border-border-primary bg-bg-primary text-text-primary pr-10 pl-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-accent-cyan/40 focus:border-accent-cyan transition-colors"
                  autoComplete="current-password"
                />
              </div>
            </div>

            {error ? (
              <div className="rounded-lg border border-status-error/30 bg-status-error/10 px-3 py-2 text-sm text-status-error" role="alert">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading}
              className="btn-base btn-primary w-full text-base font-semibold"
            >
              {loading ? 'در حال ورود…' : 'ورود به داشبورد'}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-text-tertiary">
          داده‌های این محیط نمایشی هستند و برای تصمیم‌گیری عملیاتی استفاده نشوند.
        </p>
      </div>
    </div>
  );
};

export default DemoLoginPage;
